import React, { Fragment } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import Labels from "../labels/Labels";
import { formatLocalDateTimeWithZone, parseUtcDate } from "../util/dateTime";

export default function CardTransactions(props) {
  const {
    page,
    pageSize,
    setPage,
    setPageSize,
    isLoading,
    isLoadingCount,
    isError,
    data,
    statusFilter,
    setStatusFilter,
    setPageInfoEndCursor,
    dataCount,
    pageCursorMap,
  } = props;

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("cardTransactions.id", "ID"),
      width: 70,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: "0.9rem", mb: 0 }} gutterBottom>
            {params.row.transactionKey}
          </Typography>
        );
      },
    },
    {
      field: "transactionDate",
      headerName: Labels.getLabel(
        "cardTransactions.transactionDate",
        "Transaction Date"
      ),
      width: 160,
      renderCell: (params) => {
        const transactionDate = parseUtcDate(params.row.transactionDate);
        const formattedDate = formatLocalDateTimeWithZone(transactionDate);
        const settlementDate = parseUtcDate(params?.row?.settlementDate);
        const postedFormattedDate = formatLocalDateTimeWithZone(settlementDate);
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              textAlign: "right",
              width: "100%",
            }}
          >
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{
                fontSize: "1rem",
                mb: 0,
                textAlign: "right",
                width: "100%",
              }}
              gutterBottom
            >
              {formattedDate}
            </Typography>
            {(params.row.status === "POSTED" ||
              params.row.status === "posted") &&
              params?.row?.settlementDate && (
                <Typography
                  component="span" // Change from default <p> to <span>
                  sx={{
                    fontSize: "0.6rem",
                    mb: 0,
                    textAlign: "right",
                    width: "100%",
                    color: "gray",
                  }}
                  gutterBottom
                >
                  {Labels.getLabel("cardTransactions.postedOn", "Posted on:")}{" "}
                  {postedFormattedDate}
                </Typography>
              )}
          </div>
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => {
        const formattedAmount = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(params.row.amount);
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              textAlign: "right",
              width: "100%",
            }}
          >
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{
                fontSize: "1rem",
                mb: 0,
                textAlign: "right",
                width: "100%",
              }}
              gutterBottom
            >
              {formattedAmount}
            </Typography>
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{
                display: params.row.originalAmount === 0 ? "none" : "block",
                fontSize: "0.7rem",
                mb: 0,
                textAlign: "right",
                width: "100%",
                color: "gray",
              }}
              gutterBottom
            >
              {params.row.originalAmount} {params.row.originalCurrency}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "runningBalance",
      headerName: Labels.getLabel(
        "cardTransactions.availableBalance",
        "Available Balance"
      ),
      width: 150,
      renderCell: (params) => {
        const formattedAvailableBalance = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(params.row.availableBalance);
        const formattedLedgerBalance = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(params.row.ledgerBalance);

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              textAlign: "right",
              width: "100%",
            }}
          >
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{
                fontSize: "1rem",
                mb: 0,
                textAlign: "right",
                width: "100%",
              }}
              gutterBottom
            >
              {formattedAvailableBalance}
            </Typography>
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{
                fontSize: "0.7rem",
                mb: 0,
                textAlign: "right",
                width: "100%",
                color: "gray",
              }}
              gutterBottom
            >
              {Labels.getLabel("cardTransactions.ledger", "Ledger:")}{" "}
              {formattedLedgerBalance}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "cardTransactions.description",
        "Description"
      ),
      width: 320,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "left",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{ fontSize: "0.9rem", mb: 0 }}
              gutterBottom
            >
              {params.row.concept}
            </Typography>
            <Typography
              component="span" // Change from default <p> to <span>
              sx={{
                fontSize: "0.7rem",
                mb: 0,
                fontWeight: "bold",
                color: "gray",
              }}
              gutterBottom
            >
              {params.row.category}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "observation",
      headerName: Labels.getLabel(
        "cardTransactions.observation",
        "Observation"
      ),
      width: 320,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "left",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <Typography sx={{ fontSize: "0.9rem", mb: 0 }} gutterBottom>
              {params.row.additionalInfo}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                mb: 0,
                fontWeight: "bold",
                color: "gray",
                display:
                  params.row.qualifier1 === "Manual reload" &&
                  params.row.qualifier2 === "Employer"
                    ? "none"
                    : "block",
              }}
              gutterBottom
            >
              ({params.row.merchantCode}) {params.row.merchantType}
            </Typography>
          </div>
        );
      },
    },
  ];

  React.useEffect(() => {
    console.log("Current page state:", page);
  }, [page]);

  const rowCount =
    dataCount?.data && dataCount?.data !== -1
      ? dataCount?.data
      : data?.data?.hasMore
        ? 100
        : page * pageSize + data?.data?.transactions?.length;

  return (
    <Fragment>
      {isLoading || isLoadingCount ? (
        <h4>{Labels.getLabel("cardTransactions.loading", "Loading...")}</h4>
      ) : !isError ? (
        <div style={{ height: 550, width: "100%" }}>
          <DataGrid
            columnVisibilityModel={{ id: false }}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            rows={data?.data?.transactions || []}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageSizeChange={setPageSize}
            onPageChange={(newPage) => {
              if (newPage === 0 && page - 1 !== 0) {
                return;
              }
              if (newPage > page) {
                if (page + 1 !== newPage) {
                  return;
                }
              }
              if (newPage < page) {
                newPage = page - 1;
              }

              setPage(newPage); // Allow navigation to previous pages or next if more records exist
              if (newPage > page) {
                setPageInfoEndCursor(data?.data?.pageInfoEndCursor);
              }
              if (newPage < page) {
                if (newPage === "0" || newPage === 0) {
                  setPageInfoEndCursor("");
                } else {
                  //debugger;
                  if (pageCursorMap !== undefined) {
                    const pageCursorInfo =
                      (pageCursorMap && pageCursorMap[newPage - 1]) || "";
                    setPageInfoEndCursor(pageCursorInfo["pageInfoEndCursor"]);
                  }
                }
              }
            }}
            components={{ Toolbar: GridToolbar }}
            page={page}
            componentsProps={{
              toolbar: {
                showQuickFilter: false,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            rowCount={rowCount}
            paginationMode="server"
            filterMode="server"
            keepNonExistentRowsSelected
            sx={{
              "& .MuiButton-root": {
                display: "none",
              },
              "& .MuiTablePagination-displayedRows": {
                display:
                  dataCount?.data && dataCount?.data > 0 ? "block" : "none",
              },
            }}
          />
        </div>
      ) : (
        <h4>
          {Labels.getLabel(
            "cardTransactions.error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
    </Fragment>
  );
}
